import { notification } from 'antd';

import history from '../history.js';
import globalVars from './global.js';
import type { RequestResult, MutablePayload } from '../types/domain.js';

export interface ApiRequestOptions extends Omit<RequestInit, 'body'> {
  body?: MutablePayload;
  noCSRF?: boolean;
}

export class HttpStatusError extends Error {
  readonly response: Response;

  constructor(statusText: string, response: Response) {
    super(statusText);
    this.name = 'HttpStatusError';
    this.response = response;
  }
}

function checkStatus(response: Response): Response {
  if (response.status >= 200 && response.status < 300) return response;

  let statusText = response.statusText;
  if (!response.url.includes('/api/user/session') || response.status !== 500) {
    notification.destroy();
    notification.error({
      message: `请求错误 ${response.status}: ${response.url}`,
      description: response.statusText,
    });
  } else {
    globalVars.userID = null;
    globalVars.username = null;
    globalVars.nickName = null;
    globalVars.avatar = null;
    globalVars.isAdmin = null;
    statusText = '该账号已退出，请重新登录';
  }

  throw new HttpStatusError(statusText, response);
}

function shouldReportSessionError(url: string, error: unknown): boolean {
  return !(
    url.includes('/api/user/session') &&
    error instanceof HttpStatusError &&
    error.response.status === 500
  );
}

export default function request<T>(
  url: string,
  options: ApiRequestOptions = {},
): Promise<RequestResult<T>> | undefined {
  const { body: requestBody, noCSRF: _noCSRF, ...requestOptions } = options;
  void _noCSRF;
  const newOptions: RequestInit = {
    credentials: 'same-origin',
    ...requestOptions,
  };

  if (options.method && ['POST', 'PUT', 'DELETE'].includes(options.method)) {
    const method = options.method.toLowerCase();
    const body = requestBody ?? {};

    if (!options.noCSRF && ['post', 'put', 'patch', 'delete'].includes(method)) {
      const csrfTokenPart = document.cookie.match(/csrfToken=([^;]+)/u);
      if (!csrfTokenPart?.[1]) {
        window.alert('在调用POST/PUT/PATCH必须进行一次GET获取Token，否则无法获知CSRF TOKEN');
        return undefined;
      }
      body['_csrf'] = csrfTokenPart[1];
    }

    newOptions.headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json; charset=utf-8',
      ...options.headers,
    };
    newOptions.body = JSON.stringify(body);
  }

  return window
    .fetch(url, newOptions)
    .then(checkStatus)
    .then(async (response) => (await response.json()) as T)
    .catch((error: unknown) => {
      if (shouldReportSessionError(url, error)) {
        notification.destroy();
        notification.error({
          message: `请求错误: ${url}`,
          description: error instanceof Error ? error.message : String(error),
        });
      }
      return error instanceof Error ? error : new Error(String(error));
    })
    .then((result) => {
      if (
        window.location.pathname.includes('home') ||
        window.location.pathname.includes('question') ||
        url.includes('log')
      ) {
        return result;
      }

      if (
        typeof result === 'object' &&
        result !== null &&
        'code' in result &&
        result.code === 400 &&
        'message' in result &&
        (result.message === '用户登录过期，请重新登录' ||
          result.message === '没有用户登录信息' ||
          result.message === '您访问的功能需要登陆后使用！')
      ) {
        notification.destroy();
        notification.error({ message: String(result.message) });
        history.replace('/home');
        return {} as T;
      }

      return result;
    });
}
